"use strict";

class RangeSlider extends HTMLElement {
  static get observedAttributes() {
    return ['data-min', 'data-max', 'data-step', 'data-value-min', 'data-value-max'];
  }
  constructor() {
    super();
    this.clamp = (num, min, max) => {
      return Math.min(Math.max(num, min), max);
    };
    this.normalize01 = (val, min, max) => {
      let normal = (val - min) / (max - min);
      let clamp = Math.min(Math.max(normal, 0), 1);
      return +clamp.toFixed(3);
    };
    this.precision = a => {
      if (!isFinite(a)) return 0;
      let e = 1,
        p = 0;
      while (Math.round(a * e) / e !== a) {
        e *= 10;
        p++;
      }
      return p;
    };
    this.roundToStep = num => {
      let step = +this.dataset.step;
      return +num.toFixed(this.precision(step));
    };
    this.snapToStep = num => {
      let step = +this.dataset.step;
      let snap = Math.round(num / step) * step;
      return +snap.toFixed(this.precision(step));
    };
    this.waitForElement = selector => {
      return new Promise(resolve => {
        if (document.querySelector(selector)) {
          return resolve(document.querySelector(selector));
        }
        const observer = new MutationObserver(mutations => {
          if (document.querySelector(selector)) {
            resolve(document.querySelector(selector));
            observer.disconnect();
          }
        });
        observer.observe(document.body, {
          childList: true,
          subtree: true
        });
      });
    };
  }
  redrawHandles() {
    if (!this.elHandleMin || !this.elHandleMax) return;
    let maxWidth = this.offsetWidth;
    let halfHandle = this.elHandleMin.offsetWidth / 2;
    let normalizedMin = this.normalize01(this.valueMin, +this.dataset.min, +this.dataset.max) * +this.dataset.max;
    let normalizedMax = this.normalize01(this.valueMax, +this.dataset.min, +this.dataset.max) * +this.dataset.max;
    let handleMinX = normalizedMin * +maxWidth / this.dataset.max;
    let handleMaxX = normalizedMax * +maxWidth / this.dataset.max;
    this.elHandleMin.style.left = this.roundToStep(handleMinX - halfHandle) + "px";
    this.elHandleMax.style.left = this.roundToStep(handleMaxX - halfHandle) + "px";
  }
  redrawTrack() {
    if (!this.elTrackFill || !this.elTrack) return;
    let halfHandle = this.elHandleMin.offsetWidth / 2;
    let maxWidth = this.offsetWidth;
    let trackX = this.elHandleMin.offsetLeft + halfHandle;
    trackX = this.clamp(trackX, -halfHandle, maxWidth + halfHandle);
    let trackW = this.elHandleMax.offsetLeft - trackX + halfHandle;
    trackW = this.clamp(trackW, 0, maxWidth);
    this.elTrackFill.style.left = trackX + "px";
    this.elTrackFill.style.width = trackW + "px";
  }
  bindInputs() {
    const root = this;
    const minInput = document.querySelector(this.dataset.bindMin);
    const maxInput = document.querySelector(this.dataset.bindMax);
    if (!minInput || !maxInput) return;
    minInput.addEventListener("change", function (event) {
      root.dataset.valueMin = +event.target.value;
    });
    maxInput.addEventListener("change", function (event) {
      root.dataset.valueMax = +event.target.value;
    });
  }
  recheckInputs() {
    let blockEvent = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    const minInput = document.querySelector(this.dataset.bindMin);
    const maxInput = document.querySelector(this.dataset.bindMax);
    const form = document.querySelector(this.dataset.bindForm);
    const formEvent = new Event("input");
    if (!minInput || !maxInput || !form) return;
    minInput.value = this.valueMin;
    maxInput.value = this.valueMax;
    setTimeout(function () {
      if (!blockEvent) form.dispatchEvent(formEvent);
    }, 0);
  }
  connectedCallback() {
    this.root = this;
    this.elHandleMin = this.querySelector("[data-handle-min]");
    this.elHandleMax = this.querySelector("[data-handle-max]");
    this.elTrackFill = this.querySelector("[data-track-fill]");
    this.elTrack = this.querySelector("[data-track]");
    if (!this.elHandleMin || !this.elHandleMax || !this.elTrackFill || !this.elTrack) return;
    this.valueMin = +this.dataset.valueMin;
    this.valueMax = +this.dataset.valueMax;
    this.handleMaxX = 0;
    this.handleMinX = 100;
    this._mousedownHandler = ev => {
      this.handleMousedown(ev, this.root);
    };
    this._mousemoveHandler = ev => {
      this.handleMousemove(ev, this.root);
    };
    this._mouseupHandler = ev => {
      this.handleMouseup(ev, this.root);
    };
    this.addEventListener("mousedown", this._mousedownHandler);
    this.addEventListener("touchstart", this._mousedownHandler, {
      passive: true
    });
    document.addEventListener("mousemove", this._mousemoveHandler);
    document.addEventListener("touchmove", this._mousemoveHandler, {
      passive: true
    });
    document.addEventListener("mouseup", this._mouseupHandler);
    document.addEventListener("touchend", this._mouseupHandler, {
      passive: true
    });
    this.bindInputs();
    this.redrawHandles();
    this.redrawTrack();
  }
  attributeChangedCallback(attrName, oldVal, newVal) {
    switch (attrName) {
      case "data-value-min":
        this.valueMin = +this.dataset.valueMin;
        this.redrawHandles();
        this.redrawTrack();
        this.recheckInputs(true);
        break;
      case "data-value-max":
        this.valueMax = +this.dataset.valueMax;
        this.redrawHandles();
        this.redrawTrack();
        this.recheckInputs(true);
        break;
      case "data-min":
      case "data-max":
        this.redrawHandles();
        this.redrawTrack();
        break;
      default:
        break;
    }
  }
  onchangeCallback() {
    this.recheckInputs(false);
  }
  handleMousedown(event, root) {
    if (event.target.hasAttribute("data-handle-min")) {
      root.activeHandle = root.elHandleMin;
      root.activeHandleType = "min";
      if (event.type == "mousedown") {
        root.activeHandleSX = event.clientX - root.activeHandle.getBoundingClientRect().left;
      } else {
        root.activeHandleSX = event.targetTouches[0].clientX - root.activeHandle.getBoundingClientRect().left;
      }
    } else if (event.target.hasAttribute("data-handle-max")) {
      root.activeHandle = root.elHandleMax;
      root.activeHandleType = "max";
      if (event.type == "mousedown") {
        root.activeHandleSX = event.clientX - root.activeHandle.getBoundingClientRect().left;
      } else {
        root.activeHandleSX = event.targetTouches[0].clientX - root.activeHandle.getBoundingClientRect().left;
      }
    } else {
      delete root.activeHandle;
      delete root.activeHandleType;
      root.activeHandleSX = 0;
    }
    let halfHandle = root.elHandleMin.offsetWidth / 2;
    root.handleMinStart = -halfHandle;
    root.handleMinEnd = +root.offsetWidth - halfHandle;
    root.handleMaxStart = -halfHandle;
    root.handleMaxEnd = +root.offsetWidth - halfHandle;
  }
  handleMousemove(event, root) {
    let handle = root.activeHandle;
    let handleType = root.activeHandleType;
    let halfHandle = root.elHandleMin.offsetWidth / 2;
    let dataMin = +root.dataset.min;
    let dataMax = +root.dataset.max;
    if (!handle) return;
    let handleX;
    if (event.type == "mousemove") {
      handleX = event.clientX - root.offsetLeft - root.activeHandleSX;
    } else {
      handleX = event.targetTouches[0].clientX - root.offsetLeft - root.activeHandleSX;
    }
    handleX = root.clamp(handleX, root.handleMinStart, root.handleMinEnd);
    if (handle && handleType == "min") {
      handleX = root.clamp(handleX, root.handleMinStart, root.elHandleMax.offsetLeft - halfHandle * 2 + 2);
    } else if (handle && handleType == "max") {
      handleX = root.clamp(handleX, root.elHandleMin.offsetLeft + halfHandle * 2 - 2, root.handleMinEnd);
    }
    let percentage = root.normalize01(handleX, -halfHandle, root.offsetWidth - halfHandle) * 100;
    let newValue = +(percentage * (dataMax - dataMin) / 100) + dataMin;
    newValue = root.clamp(newValue, dataMin, dataMax);
    newValue = root.roundToStep(newValue);
    newValue = root.snapToStep(newValue);
    if (handleType == "min") {
      root.dataset.valueMin = newValue;
      root.onchangeCallback();
    } else if (handleType == "max") {
      root.dataset.valueMax = newValue;
      root.onchangeCallback();
    }
  }
  handleMouseup(event, root) {
    delete root.activeHandle;
    delete root.activeHandleType;
    root.activeHandleSX = 0;
  }
  disconnectedCallback() {
    this.removeEventListener("mousedown", this._mousedownHandler);
    this.removeEventListener("touchstart", this._mousedownHandler, {
      passive: true
    });
    document.removeEventListener("mousemove", this._mousemoveHandler);
    document.removeEventListener("touchmove", this._mousemoveHandler, {
      passive: true
    });
    document.removeEventListener("mouseup", this._mouseupHandler);
    document.removeEventListener("touchend", this._mouseupHandler, {
      passive: true
    });
  }
}
customElements.define("range-slider", RangeSlider);
window.addEventListener("resize", function (event) {
  const rangeSlider = document.querySelector(".js-facets-range-slider");
  const rangeSliderMobile = document.querySelector(".js-facets-range-slider-mob");
  if (rangeSlider && rangeSlider.redrawTrack && rangeSlider.redrawHandles) {
    rangeSlider.redrawHandles();
    rangeSlider.redrawTrack();
  }
  if (rangeSliderMobile && rangeSliderMobile.redrawTrack && rangeSliderMobile.redrawHandles) {
    rangeSliderMobile.redrawHandles();
    rangeSliderMobile.redrawTrack();
  }
});
(() => {
  const mobileSortBy = document.getElementById("SortBy-mobile");
  if (mobileSortBy) {
    mobileSortBy.addEventListener('input', () => {
      const desktopSortBy = document.getElementById("SortBy");
      if (desktopSortBy) {
        desktopSortBy.value = mobileSortBy.value;
      }
      const mobileFacetForm = document.getElementById("FacetFiltersFormMobile");
      if (mobileFacetForm) {
        desktopSortBy.dispatchEvent(new Event('input', {
          bubbles: true
        }));
      }
    });
  }
})();